import { useEffect, useRef } from "react";
import { App as CapacitorApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import {
  clearPendingOAuth,
  getOAuthCallbackParams,
  getPendingOAuthRedirectPath,
  hasPendingOAuthAttempt,
  isNativeOAuthCallbackUrl,
  validatePendingOAuthState,
} from "@/lib/nativeOAuth";

const waitForConfirmedSession = async () => {
  for (let attempt = 0; attempt < 8; attempt += 1) {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    if (data.session) return;
    await new Promise((resolve) => window.setTimeout(resolve, 150));
  }
  throw new Error("OAuth session was not persisted.");
};

const NativeOAuthHandler = () => {
  const handledUrls = useRef(new Set<string>());
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const finishNativeOAuth = async (url: string) => {
      if (!isNativeOAuthCallbackUrl(url) || handledUrls.current.has(url)) {
        return;
      }

      handledUrls.current.add(url);

      try {
        const closeOAuthBrowser = () => {
          void Browser.close().catch(() => {});
        };
        closeOAuthBrowser();
        window.setTimeout(closeOAuthBrowser, 350);
        window.setTimeout(closeOAuthBrowser, 1000);

        const params = getOAuthCallbackParams(url);
        const state = params.get("native_state") || params.get("state");
        if (!validatePendingOAuthState(state)) {
          // iOS can replay a completed launch URL when the app starts again.
          if (!hasPendingOAuthAttempt()) return;
          throw new Error(t("nativeOAuth.sessionNotReady"));
        }
        const error = params.get("error") || params.get("error_code");
        if (error) {
          throw new Error(params.get("error_description") || error);
        }

        const accessToken = params.get("access_token");
        const refreshToken = params.get("refresh_token");
        const code = params.get("code");

        if (accessToken && refreshToken) {
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (sessionError) throw sessionError;
        } else if (code) {
          const { error: sessionError } = await supabase.auth.exchangeCodeForSession(code);
          if (sessionError) throw sessionError;
        } else {
          throw new Error("OAuth callback did not include a session.");
        }

        await waitForConfirmedSession();

        navigate(getPendingOAuthRedirectPath("/search", state), { replace: true });
        clearPendingOAuth();
      } catch (error) {
        clearPendingOAuth();
        toast({
          title: t("nativeOAuth.failedTitle"),
          description: error instanceof Error ? error.message : t("nativeOAuth.failedDesc"),
          variant: "destructive",
        });
        navigate("/auth", { replace: true });
      }
    };

    void CapacitorApp.getLaunchUrl().then((launchUrl) => {
      if (launchUrl?.url) {
        void finishNativeOAuth(launchUrl.url);
      }
    });

    const listener = CapacitorApp.addListener("appUrlOpen", ({ url }) => {
      void finishNativeOAuth(url);
    });

    return () => {
      void listener.then((handle) => handle.remove());
    };
  }, [navigate, toast, t]);

  return null;
};

export default NativeOAuthHandler;
